/**
 * The season pick'em leaderboard, top three, with a way through to the rest.
 *
 * Fetched here rather than folded into `/api/state`: it is one query, it is
 * not needed by any other screen, and putting it in the single state payload
 * would make every page load pay for it (05-api-surface.md §2 is explicit that
 * the state fetch carries what the home screen needs, and this is an addition
 * to that screen, not to the app).
 *
 * D80 — points and points-per-pick are always shown together. A late joiner
 * has fewer points for an honest reason, and showing the total alone turns
 * them into a bad picker.
 *
 * Nobody is invented. Before anyone has picked, the module says so rather
 * than rendering placeholder rows with made-up names.
 */

import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import type { StandingRow } from '@shared/types'
import { GET } from '../lib/api'
import { Card, Eyebrow, SkeletonCard } from './index'

interface StandingsResponse {
  standings: StandingRow[]
}

/** ".787" reads better than "0.787" in a narrow column. */
function perPick(n: number): string {
  if (!Number.isFinite(n)) return '-'
  const s = n.toFixed(3)
  return s.startsWith('0') ? s.slice(1) : s
}

export function SeasonStandings({ season, style }: { season?: number; style?: React.CSSProperties }) {
  const navigate = useNavigate()
  const [rows, setRows] = useState<StandingRow[] | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    const ac = new AbortController()
    void (async () => {
      try {
        const qs = new URLSearchParams({ scope: 'season' })
        if (season) qs.set('season', String(season))
        const res = await GET<StandingsResponse>(`/api/picks/standings?${qs.toString()}`, ac.signal)
        setRows(res.standings ?? [])
      } catch {
        if (!ac.signal.aborted) setFailed(true)
      }
    })()
    return () => ac.abort()
  }, [season])

  // A leaderboard that could not load is not worth a broken card on the home
  // screen. It simply is not there this time.
  if (failed) return null
  if (rows === null) return <SkeletonCard lines={3} />

  const top = rows.slice(0, 3)

  return (
    <Card style={{ padding: 18, borderRadius: 'var(--radius-card)', ...style }}>
      <Eyebrow large>Season standings</Eyebrow>

      {top.length === 0 ? (
        <div style={{ fontSize: 14, color: 'var(--dim)', marginTop: 12, lineHeight: 1.5 }}>
          Nobody has made a pick yet. First one on the board sets the pace.
        </div>
      ) : (
        <div style={{ marginTop: 12 }}>
          {top.map((r, i) => (
            <div
              key={r.userId}
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '10px 0',
                borderTop: i === 0 ? undefined : '1px solid var(--line)',
              }}
            >
              <span
                className="mono"
                style={{ fontSize: 13, fontWeight: 700, width: 18, flex: '0 0 auto', color: 'var(--dim)' }}
              >
                {r.rank}
              </span>
              <Link
                to={`/u/${encodeURIComponent(r.userId)}`}
                style={{
                  flex: '1 1 auto', minWidth: 0, fontSize: 15,
                  fontWeight: r.isViewer ? 700 : 500, color: 'var(--fg)',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}
              >
                {r.displayName}{r.isViewer ? ' (you)' : ''}
              </Link>
              <span className="mono" style={{ fontSize: 15, fontWeight: 700, flex: '0 0 auto' }}>
                {r.points}
              </span>
              <span
                className="mono"
                style={{ fontSize: 12, color: 'var(--dim)', width: 38, textAlign: 'right', flex: '0 0 auto' }}
              >
                {perPick(r.perPick)}
              </span>
            </div>
          ))}
        </div>
      )}

      <button
        onClick={() => navigate('/picks/standings')}
        style={{
          display: 'block', width: '100%', marginTop: 16, padding: 15, minHeight: 48,
          borderRadius: 'var(--radius-btn)', background: 'var(--fill)', color: 'var(--ink)',
          fontSize: 15, fontWeight: 700, textAlign: 'center',
        }}
      >
        Full standings
      </button>
    </Card>
  )
}
